import { apiClient } from '@/lib/api/client';
import type {
  WalletBalance,
  Transaction,
  TransactionStatus,
  Withdrawal,
  TransactionFilters,
  WalletState,
} from '../types/wallet.types';

// Note on payouts: `requestTransfer` (POST /payment/seller/request-transfer)
// has been removed — that endpoint is not implemented on the backend. Use
// `payoutService.requestPayout()` from `@/features/payouts` which targets the
// modern `POST /payment/seller/payouts` route.

interface ApiData<T> {
  data: T;
}

// Doc 59, Task 5b.3c — entrée de `GET /payment/seller/payouts-history`
// (historique payout local, `SellerPayout`). Remplace `CompanyTransfer`
// legacy (`/payment/company/transfers`), inerte côté serveur (liste
// toujours vide) — dernier consommateur web-pro de CompanyTransfer.
interface SellerPayoutHistoryEntry {
  volovoloPayoutId: string;
  amount: number;
  currency: string;
  status: string;
  isReversal: boolean;
  createdAt: string;
}

// Statut payout (`SellerPayout.status`) → `TransactionStatus` affiché dans
// le wallet. `isReversal` prime sur le statut brut : un retrait annulé
// s'affiche comme « cancelled » quel que soit son statut sous-jacent.
function mapPayoutStatus(status: string, isReversal: boolean): TransactionStatus {
  if (isReversal) return 'cancelled';
  switch (status) {
    case 'COMPLETED':
      return 'completed';
    case 'FAILED':
      return 'failed';
    case 'CANCELED':
    case 'CANCELLED':
      return 'cancelled';
    // 'REQUESTED' | 'PENDING_APPROVAL' | 'EXECUTING' | 'WAITING_HOLD' | tout
    // autre statut inconnu → en cours, valeur par défaut la plus sûre.
    default:
      return 'pending';
  }
}

function mapPayoutHistoryEntryToTransaction(entry: SellerPayoutHistoryEntry): Transaction {
  return {
    id: entry.volovoloPayoutId,
    type: entry.isReversal ? 'adjustment' : 'withdrawal',
    amount: entry.amount,
    netAmount: entry.amount,
    status: mapPayoutStatus(entry.status, entry.isReversal),
    description: entry.isReversal ? 'Retrait annulé (reversal)' : 'Retrait',
    createdAt: entry.createdAt,
  };
}

interface PayoutListResponse {
  data: Withdrawal[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    hasMore: boolean;
  };
}

export const walletService = {
  async getBalance(): Promise<WalletBalance | null> {
    const response = await apiClient.get<ApiData<WalletBalance>>('/payment/seller/balance');
    return response.data?.data ?? null;
  },

  async getTransactions(filters?: TransactionFilters): Promise<Transaction[]> {
    // Doc 59, Task 5b.3c — `/payment/seller/payouts-history` remplace le
    // legacy `/payment/company/transfers` (CompanyTransfer, inerte). Cet
    // endpoint n'attend aucun query param (pas de pagination serveur) : on
    // applique `filters.limit` côté client sur le résultat mappé.
    const response = await apiClient.get<ApiData<SellerPayoutHistoryEntry[]>>(
      '/payment/seller/payouts-history'
    );
    const entries = response.data?.data ?? [];
    const transactions = entries.map(mapPayoutHistoryEntryToTransaction);
    return transactions.slice(0, filters?.limit ?? 20);
  },

  async getPayouts(filters?: { page?: number; limit?: number; status?: string }): Promise<Withdrawal[]> {
    const params = new URLSearchParams();
    // page and limit are required by the server
    params.set('page', String(filters?.page || 1));
    params.set('limit', String(filters?.limit || 20));
    if (filters?.status) params.set('status', filters.status);

    const response = await apiClient.get<ApiData<PayoutListResponse>>(
      `/payment/payouts?${params.toString()}`
    );
    return response.data?.data?.data ?? [];
  },

  async getPayout(id: string): Promise<Withdrawal | null> {
    const response = await apiClient.get<ApiData<Withdrawal>>(`/payment/payouts/${id}`);
    return response.data?.data ?? null;
  },

  async getSellerStatus(): Promise<{ isOnboarded: boolean; status: string } | null> {
    const response = await apiClient.get<ApiData<{ isOnboarded: boolean; status: string }>>(
      '/payment/seller/status'
    );
    return response.data?.data ?? null;
  },

  async createOnboarding(): Promise<{ sellerId: string; isNew: boolean } | null> {
    const response = await apiClient.post<ApiData<{ sellerId: string; isNew: boolean }>>(
      '/payment/seller/onboarding'
    );
    return response.data?.data ?? null;
  },

  // Note: a previous `requestTransfer` calling `/payment/seller/request-transfer`
  // was removed — that route does not exist on the backend. To request a payout,
  // use `payoutService.requestPayout()` from `@/features/payouts`, which targets
  // the correct `POST /payment/seller/payouts` endpoint.

  async getWalletState(): Promise<WalletState | null> {
    const response = await apiClient.get<ApiData<WalletState>>('/payment/seller/wallet-state');
    return response.data?.data ?? null;
  },
};
